import {
  ActionIcon,
  Stack,
  Flex,
  Box,
  Modal,
  Button,
  Group,
  Text,
} from '@mantine/core';
import { CustomInput } from '../CustomInput';
import {
  IconTrash,
  IconEdit,
  IconGripVertical,
  IconCopy,
} from '@tabler/icons-react';
import { useState } from 'react';
import { getHtmlRenderer } from './utils/getHtmlRenderer';
import { useIntl } from 'react-intl';

export const DrawFields = ({
  fields,
  onFieldDelete,
  onFieldEdit,
  onFieldsUpdate,
}: {
  fields: any[];
  onFieldDelete: (field: any) => void;
  onFieldEdit: (field: any) => void;
  onFieldsUpdate: (fields: any[]) => void;
}) => {
  const intl = useIntl();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [fieldToDelete, setFieldToDelete] = useState<any | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newInputs = [...fields];
    const draggedItem = newInputs[draggedIndex];

    // Remove the dragged item
    newInputs.splice(draggedIndex, 1);
    // Insert it at the new position
    newInputs.splice(index, 0, draggedItem);

    onFieldsUpdate(newInputs);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDeleteClick = (field: any) => {
    setFieldToDelete(field);
  };

  const handleConfirmDelete = () => {
    if (fieldToDelete) {
      onFieldDelete(fieldToDelete);
      setFieldToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setFieldToDelete(null);
  };

  return (
    <>
      <Stack gap="md">
        {fields.map((field: any, index: number) => (
          <Flex
            key={field.name}
            gap="md"
            align="center"
            onDragOver={(e) => handleDragOver(e, index)}
            style={{
              opacity: draggedIndex === index ? 0.5 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            <Flex gap="xs" align="center">
              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => handleDeleteClick(field)}
              >
                <IconTrash size={16} />
              </ActionIcon>

              <ActionIcon
                size="sm"
                variant="default"
                onClick={() => {
                  onFieldEdit(field);
                }}
              >
                <IconEdit size={16} />
              </ActionIcon>

              <ActionIcon
                size="sm"
                disabled={!getHtmlRenderer(field)}
                variant="default"
                onClick={() => {
                  navigator.clipboard.writeText(getHtmlRenderer(field) || '');
                }}
              >
                <IconCopy size={16} />
              </ActionIcon>
            </Flex>
            <Box style={{ flex: 1 }}>
              <CustomInput {...field} isDisabled={true} />
            </Box>
            <div
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragEnd={handleDragEnd}
              style={{
                cursor: 'grab',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <IconGripVertical size={20} />
            </div>
          </Flex>
        ))}
      </Stack>

      <Modal
        opened={fieldToDelete !== null}
        onClose={handleCancelDelete}
        title={intl.formatMessage({ id: 'delete' })}
        centered
      >
        <Text size="sm" mb="md">
          {intl.formatMessage({ id: 'areYouSureYouWantToDelete' })}{' '}
          <strong>{fieldToDelete?.label || fieldToDelete?.title}</strong>?
        </Text>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleCancelDelete}>
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
          <Button color="red" onClick={handleConfirmDelete}>
            {intl.formatMessage({ id: 'delete' })}
          </Button>
        </Group>
      </Modal>
    </>
  );
};
